import httpClient from './httpClient';
import authzApi from "@/api/authzApi";
import {setDifferenceObject} from "@/util/log";
import {
  COUPON_LOCK,
  COUPON_LIMITED,
  COUPON_UNLOCK,
  COUPON_UNLIMITED
} from "@/model/coupon";
import router from "@/router";

const API_PREFIX = "reward/v1";

const handleApiError = (response) => {
  if (response?.data?.success === false) {
    if (response?.data?.error?.code === "TARGET_NOT_FOUND") {
      alert("존재하지 않는 쿠폰입니다.");
      router.push("../list");
    } else {
      alert(response?.data?.error?.message);
    }
  }
  return response;
}

export default {
  /* cooupon */
  getCouponList() {
    return httpClient.get(`${API_PREFIX}/coupons`)
      .then(handleApiError);
  },

  getCouponInbox(idpType, idpUserId, couponInbox, page = 0, size = 10) {
    return httpClient.post(`${API_PREFIX}/coupons/idp-type/${idpType}/idp-user-ids/${idpUserId}`, couponInbox, {params: {page: page, size: size}});
  },

  getCouponListByAdAccountId(adAccountId) {
    return httpClient.get(`${API_PREFIX}/coupons/ad-accounts/${adAccountId}`)
      .then(handleApiError);
  },

  getCouponListByAdAccountIdAndAppId(adAccountId, appId) {
    return httpClient.get(`${API_PREFIX}/coupons/ad-accounts/${adAccountId}/apps/${appId}`)
      .then(handleApiError);
  },

  getCoupon(couponId) {
    return httpClient.get(`${API_PREFIX}/coupons/${couponId}`)
      .then(handleApiError)
  },

  getCouponNumberCount(couponId) {
    return httpClient.get(`${API_PREFIX}/coupons/${couponId}/coupon-numbers`)
      .then(handleApiError);
  },

  getCouponStatistics(couponId) {
    return httpClient.get(`${API_PREFIX}/coupons/${couponId}/statistics`)
      .then(handleApiError);
  },

  getShippingListByAdAccountIdAndAppId(adAccountId, appId) {
    return httpClient.get(`${API_PREFIX}/shipping/ad-accounts/${adAccountId}/apps/${appId}`)
      .then(handleApiError);
  },

  postCoupon(coupon) {
    return httpClient.post(`${API_PREFIX}/coupons`, coupon).then(
      response => {
        if (response?.data?.status === 200) {
          const createdCoupon = response?.data?.data?.coupon;
          let couponDiff = setDifferenceObject(null, "coupon", null,
                                               createdCoupon);
          authzApi.writeModifyLog(`/${API_PREFIX}/coupons/${createdCoupon.id}`, "POST",
                                  "CP-CREATE-MT",
                                  JSON.stringify(couponDiff),
                                  {
                                    "coupon-id": createdCoupon.id
                                  });
        }
        return handleApiError(response);
      }
    );
  },

  putCoupon(coupon, originalCoupon) {
    return httpClient.put(`${API_PREFIX}/coupons/${coupon.id}`, coupon).then(
      response => {
        if (response?.data?.status === 200) {
          const modifiedCoupon = response?.data?.data?.coupon;
          let couponDiff = setDifferenceObject(null, "coupon",
                                               originalCoupon,
                                               modifiedCoupon);
          authzApi.writeModifyLog(`/${API_PREFIX}/coupons/${modifiedCoupon.id}`, "PUT",
                                  "CP-MODIFY-MT",
                                  JSON.stringify(couponDiff),
                                  {
                                    "coupon-id": modifiedCoupon.id,
                                    "ad-account-id": modifiedCoupon.adAccountId
                                  });
        }
        return handleApiError(response);
      }
    );
  },

  deleteCoupon(coupon) {
    return httpClient.delete(`${API_PREFIX}/coupons/${coupon.id}`, coupon).then(
      response => {
        if (response?.data?.status === 204) {
          let couponDiff = setDifferenceObject(null, "coupon",
                                               coupon,
                                               null);
          authzApi.writeModifyLog(`/${API_PREFIX}/coupons/${coupon.id}`, "DELETE",
                                  "CP-DELETE-MT",
                                  JSON.stringify(couponDiff),
                                  {
                                    "coupon-id": coupon.id
                                  });
        }
        return handleApiError(response);
      }
    );
  },

  lockCouponNumbers(couponId) {
    return httpClient.put(`${API_PREFIX}/coupons/${couponId}`, {confirmStatus: COUPON_LOCK})
      .then(handleApiError);
  },

  unlockCouponNumbers(couponId) {
    return httpClient.put(`${API_PREFIX}/coupons/${couponId}`, {confirmStatus: COUPON_UNLOCK})
      .then(handleApiError);
  },

  limitCouponNumbers(couponId) {
    return httpClient.put(`${API_PREFIX}/coupons/${couponId}`, {provideType: COUPON_LIMITED})
      .then(handleApiError);
  },

  unlimitCouponNumbers(couponId) {
    return httpClient.put(`${API_PREFIX}/coupons/${couponId}`, {provideType: COUPON_UNLIMITED})
      .then(handleApiError);
  },

  uploadCouponImage(image) {
    let formData = new FormData();
    formData.append("file", image);
    return httpClient.post(`${API_PREFIX}/coupons/images`,
                           formData,
                           {
                             headers: {
                               "Content-Type": "multipart/form-data"
                             }
                           })
      .then(handleApiError);
  },

  uploadCouponNumber(couponId, file, transactionId, onUploadProgressCallback, cancelToken) {
    let formData = new FormData();
    formData.append("file", file);
    return httpClient.post(
      `${API_PREFIX}/coupons/${couponId}/coupon-numbers?transactionId=${transactionId}&menu-code=CP-UPLOAD-NB&coupon-id=${couponId}`,
      formData,
      {
        cancelToken: cancelToken,
        headers: {
          "Content-Type": "multipart/form-data",
          skipAlert: true
        },
        onUploadProgress: onUploadProgressCallback
      });
  },

  deleteCouponNumber(couponId) {
    return httpClient.delete(
      `${API_PREFIX}/coupons/${couponId}/coupon-numbers?menu-code=CP-DELETE-NB&coupon-id=${couponId}`
    ).then(handleApiError)
  },

  checkCouponUploaded(couponId, transactionId) {
    return httpClient.get(
      `${API_PREFIX}/coupons/${couponId}/coupon-numbers/transactions/${transactionId}`)
      .then(handleApiError);
  },

  /* shipping */
  getShipping(shippingId) {
    return httpClient.get(`${API_PREFIX}/shipping/${shippingId}`)
      .then(handleApiError)
  },

  getShippingList() {
    return httpClient.get(`${API_PREFIX}/shipping`)
      .then(handleApiError);
  },

  getShippingListByAdAccountId(adAccountId) {
    return httpClient.get(`${API_PREFIX}/shipping/ad-accounts/${adAccountId}`)
      .then(handleApiError);
  },

  postShipping(shipping) {
    return httpClient.post(`${API_PREFIX}/shipping`, shipping).then(
      response => {
        if (response?.data?.status === 200) {
          const createdShipping = response?.data?.data?.shipping;
          let shippingDiff = setDifferenceObject(null, "shipping", null,
                                               createdShipping);
          authzApi.writeModifyLog(`/${API_PREFIX}/shipping/${createdShipping.id}`, "POST",
                                  "SP-CREATE-MT",
                                  JSON.stringify(shippingDiff),
                                  {
                                    "shipping-id": createdShipping.id
                                  });
        }
        return handleApiError(response);
      }
    );
  },

  putShipping(shipping, originalShipping) {
    return httpClient.put(`${API_PREFIX}/shipping/${shipping.id}`, shipping).then(
      response => {
        if (response?.data?.status === 200) {
          const modifiedShipping = response?.data?.data?.shipping;
          let shippingDiff = setDifferenceObject(null, "shipping",
                                               originalShipping,
                                               modifiedShipping);
          authzApi.writeModifyLog(`/${API_PREFIX}/shipping/${modifiedShipping.id}`, "PUT",
                                  "SP-MODIFY-MT",
                                  JSON.stringify(shippingDiff),
                                  {
                                    "shipping-id": modifiedShipping.id,
                                    "ad-account-id": modifiedShipping.adAccountId
                                  });
        }
        return handleApiError(response);
      }
    );
  },

  deleteShipping(shipping) {
    return httpClient.delete(`${API_PREFIX}/shipping/${shipping.id}`, shipping).then(
      response => {
        if (response?.data?.status === 204) {
          let shippingDiff = setDifferenceObject(null, "shipping",
                                                 shipping,
                                               null);
          authzApi.writeModifyLog(`/${API_PREFIX}/shipping/${shipping.id}`, "DELETE",
                                  "CP-DELETE-MT",
                                  JSON.stringify(shippingDiff),
                                  {
                                    "shipping-id": shipping.id
                                  });
        }
        return handleApiError(response);
      }
    );
  },

  //TODO: 네이밍

  lockShipping(shippingId) {
    return httpClient.put(`${API_PREFIX}/shipping/${shippingId}`, {confirmStatus: COUPON_LOCK})
      .then(handleApiError);
  },

  unlockShipping(shippingId) {
    return httpClient.put(`${API_PREFIX}/shipping/${shippingId}`, {confirmStatus: COUPON_UNLOCK})
      .then(handleApiError);
  },

  getShippingUrlCount(shippingId) {
    return httpClient.get(`${API_PREFIX}/shipping/${shippingId}/shipping-urls`)
        .then(handleApiError)
  },

  getAvailableShippingUrl(shippingId, start = 0, end = 1000) {
    return httpClient.get(`${API_PREFIX}/shipping/${shippingId}/available-urls`, {params: {start: start, end: end}})
      .then(handleApiError)
  },

  uploadShippingUrl(shippingId, file, transactionId, onUploadProgressCallback, cancelToken) {
    let formData = new FormData();
    formData.append("file", file);
    formData.append("shippingId", shippingId);
    formData.append("transactionId", transactionId);
    return httpClient.post(
      `${API_PREFIX}/shipping/${shippingId}/shipping-urls`,
      formData,
      {
        cancelToken: cancelToken,
        headers: {
          "Content-Type": "multipart/form-data",
          skipAlert: true
        },
        onUploadProgress: onUploadProgressCallback
      });
  },

  deleteShippingUrls(shippingId) {
    return httpClient.delete(
      `${API_PREFIX}/shipping/${shippingId}/shipping-urls`
    ).then(handleApiError)
  },

  getShippingUrlStatistics(shippingId) {
    return httpClient.get(`${API_PREFIX}/shipping/${shippingId}/statistics`)
      .then(handleApiError);
  },

  getShippingResult(shippingId) {
    return httpClient.get(`${API_PREFIX}/shipping/${shippingId}/alimtalk-results`)
      .then(handleApiError);
  },

  checkShippingUrlsUploaded(shippingId, transactionId) {
    return httpClient.get(
      `${API_PREFIX}/shipping/${shippingId}/shipping-urls/transactions/${transactionId}`)
      .then(handleApiError);
  },

  /* etc */

  checkKakaoEmoticonValidation(kakaoRewardApiKey) {
    return httpClient.post(`${API_PREFIX}/rewards/validate-api-key`,
                           {
                             rewardApiKey: kakaoRewardApiKey,
                             rewardType: "KAKAO_EMOTICON"
                           })
      .then(handleApiError);
  },

  checkRewardValidation(kakaoRewardApiKey, rewardType) {
    return httpClient.post(`${API_PREFIX}/rewards/validate-api-key`,
      {
        rewardApiKey: kakaoRewardApiKey,
        rewardType: rewardType
      })
      .then(handleApiError);
  },
  sendReward(rewardId, idpType, idpUserId, caller, extraInfo) {
    return httpClient.post(`${API_PREFIX}/delivery`,
      {
        rewardId: rewardId,
        idpUserId: idpUserId,
        caller: caller,
        idpType: idpType,
        extraInfo: extraInfo
      })
      .then(handleApiError);
  },
  getDeliveryInfo(deliveryId) {
    return httpClient.get(`${API_PREFIX}/delivery/${deliveryId}`)
      .then(handleApiError);
  },
}
